import { Alert, Divider, Table } from 'antd'
import { useMemo } from 'react'
import { Title } from '../styles'
import { SearchIcon, SearchInput, TableWrapper } from './styles'
import { ColumnsType } from 'antd/es/table'
import { MedicalExpenseTableHook, BenefitItem } from './MedicalExpenseTable.hook'
import MedicalExpenseInput from '../MedicalExpenseInput'
import { INCOMPLETE_COVERAGE_DATA_MESSAGE } from '@/libs/utils/claim-error-notification.util'

function MedicalExpenseTable() {
  const {
    isLoading,
    isCreateOrEditMode,
    isNewPolicyType,
    isCoverageDataIncomplete,
    filteredData,
    searchText,
    setSearchText,
    searchPlaceholder,
    updateMedicalExpenseItem,
    getMedicalExpenseTotal,
    getMedicalExpenseItem,
    moneyFormat,
  } = MedicalExpenseTableHook()

  const columns: ColumnsType<BenefitItem> = [
    {
      title: 'ลำดับ',
      dataIndex: 'no',
      key: 'no',
      width: 70,
    },
    {
      title: isNewPolicyType ? 'Interest code' : 'Claim type code',
      dataIndex: 'code',
      key: 'code',
      width: 190,
    },
    {
      title: 'รายการ',
      dataIndex: 'description',
      key: 'description',
      width: 400,
    },
    ...(isNewPolicyType
      ? [
          {
            title: 'จำนวนที่เบิกได้สูงสุด (บาท)',
            dataIndex: 'maxClaimAmount',
            key: 'maxClaimAmount',
            width: 180,
            render: (value: number | string) =>
              typeof value === 'number' ? value.toLocaleString() : value,
          },
          {
            title: 'จำนวนยอดคงเหลือ (บาท)',
            dataIndex: 'remainingClaimBalance',
            key: 'remainingClaimBalance',
            width: 180,
            render: (value: number | string) =>
              typeof value === 'number' ? value.toLocaleString() : value,
          },
          {
            title: 'จำนวนยอดตามใบเสร็จ (บาท)',
            dataIndex: 'receiptAmount',
            key: 'receiptAmount',
            width: 180,
            render: (value: number, record: BenefitItem) => {
              const expenseItem = getMedicalExpenseItem(record.key)
              return (
                <MedicalExpenseInput
                  value={expenseItem?.receiptAmount ?? 0}
                  disabled={!isCreateOrEditMode}
                  onChange={(value) => updateMedicalExpenseItem(record.key, 'receiptAmount', value)}
                />
              )
            },
          },
          {
            title: 'จำนวนที่คุ้มครอง (บาท)',
            dataIndex: 'coverageAmount',
            key: 'coverageAmount',
            width: 180,
            render: (value: number, record: BenefitItem) => {
              const expenseItem = getMedicalExpenseItem(record.key)
              return (
                <MedicalExpenseInput
                  value={expenseItem?.coverageAmount ?? 0}
                  disabled={!isCreateOrEditMode}
                  onChange={(value) =>
                    updateMedicalExpenseItem(record.key, 'coverageAmount', value)
                  }
                />
              )
            },
          },
        ]
      : [
          {
            title: 'จำนวนที่คุ้มครอง (บาท)',
            dataIndex: 'coverageAmount',
            key: 'coverageAmount',
            width: 180,
            render: (value: number, record: BenefitItem) => {
              const expenseItem = getMedicalExpenseItem(record.key)
              return (
                <MedicalExpenseInput
                  value={expenseItem?.coverageAmount ?? 0}
                  disabled={!isCreateOrEditMode}
                  onChange={(value) =>
                    updateMedicalExpenseItem(record.key, 'coverageAmount', value)
                  }
                />
              )
            },
          },
        ]),
  ]

  // Collect all expandable keys (all levels) so nested children render by default
  const expandedRowKeys = useMemo(() => {
    const keys: Array<string | number> = []
    const walk = (items: BenefitItem[]) => {
      items.forEach((item) => {
        if (item.children && item.children.length > 0) {
          keys.push(item.key)
          walk(item.children)
        }
      })
    }
    walk(filteredData as BenefitItem[])
    return keys
  }, [filteredData])

  return (
    <>
      <Divider />
      <Title>รายการค่ารักษาพยาบาล</Title>
      <SearchInput
        placeholder={searchPlaceholder}
        allowClear
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        prefix={<SearchIcon />}
        disabled={!isCreateOrEditMode}
      />
      <TableWrapper>
        {isCoverageDataIncomplete ? (
          <Alert type="error" message={INCOMPLETE_COVERAGE_DATA_MESSAGE} />
        ) : (
          <Table
            columns={columns}
            dataSource={filteredData}
            pagination={false}
            size="small"
            loading={isLoading}
            scroll={{ y: 608 }}
            expandable={
              isNewPolicyType === true
                ? {
                    defaultExpandAllRows: true,
                    expandRowByClick: false,
                    expandIcon: () => null,
                    expandIconColumnIndex: 1, // shift indent to Interest code column
                    childrenColumnName: 'children',
                    rowExpandable: (record) => !!(record.children && record.children.length > 0),
                    expandedRowKeys,
                  }
                : undefined
            }
            summary={() => (
              <Table.Summary fixed="bottom">
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={isNewPolicyType ? 6 : 3} align="right">
                    จำนวนเบิกจ่ายสินไหมทดแทนรวม
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={isNewPolicyType ? 6 : 3} align="right">
                    {moneyFormat.format(getMedicalExpenseTotal())}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        )}
      </TableWrapper>
      <Divider />
    </>
  )
}

export default MedicalExpenseTable
